import React, { useEffect, useRef, useContext, Fragment, useMemo } from "react";
import { Formik, Form } from "formik";
import DominantMediaField from "Screens/Article/Fields/DominantMediaField";
import TextInput from "components/FormElements/TextInput";
import FastTextInput from "components/FormElements/FastTextInput";
import Label from "components/FormElements/Label";
import { useAuth } from "hooks/Auth";
import ArticleEditor from "components/FormElements/ArticleEditor";
import WorkflowField from "Screens/Article/Fields/WorkflowField";
import TagField from "Screens/Article/Fields/TagField";
import UuidField from "Screens/Article/Fields/UuidField";
import ArticleMeta from "Screens/Article/Fields/ArticleMeta";
import ArticleToolbar from "Screens/Article/ArticleToolbar";
import TabContainer, { TabPanel } from "components/Surfaces/TabContainer";
import VersionStatusField from "Screens/Article/Fields/VersionStatusField";
import StaticDateField from "Screens/Article/Fields/StaticDateField";
import { ContentContext } from "context/Content";
import PublishButton from "Screens/Article/Fields/PublishButton";
import TextArea from "components/FormElements/TextArea";
import ExportButton from "Screens/Article/Fields/ExportButton";
import SocialPreviews from "Screens/Article/SocialPreviews";
import HistoryButton from "Screens/Article/Fields/HistoryButton";
import LayoutTemplateField from "./Fields/LayoutTemplateField";
import { useRegisterActions, Priority } from "kbar";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import UrlField from "./Fields/UrlField";
import { LockClosedIcon } from "@heroicons/react/outline";
import { fromSqlDateTime } from "helpers/Date";
import { useSelector } from "react-redux";

function EditForm(props) {
    const auth = useAuth();
    const formRef = useRef();
    const { state } = useContext(ContentContext);
    const settings = useSelector((state) => state.contentFlow?.settings);

    const kbarActions = useMemo(() => {
        if (
            state.content.lock &&
            state.content.lock.user.uuid === auth.user.uuid
        ) {
            return [
                {
                    id: "pageSave",
                    name: "Save",
                    shortcut: ["Meta+Enter"],
                    keywords: "save",
                    perform: () => {
                        setTimeout(() => {
                            formRef.current.submitForm();
                        }, 250);
                    },
                    priority: Priority.HIGH,
                },
            ];
        } else {
            return [];
        }
    }, [state.content, auth.user]);

    useRegisterActions(kbarActions, [kbarActions]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!state.isUserEditable) {
                return;
            }
            if (!props.onAutoSave) {
                return;
            }
            if (!formRef.current || !state.content.uuid) {
                return;
            }
            if (!formRef.current.dirty) {
                return;
            }
            props.onAutoSave(formRef.current.values);
        }, 2500);
        return () => clearInterval(interval);
    });

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: state.formValues,
            });
        });
    }, [state.loading, state.formValues]);

    return (
        <Formik
            innerRef={formRef}
            initialValues={state.formValues}
            onSubmit={props.onSubmit}
            enableReinitialize={false}
        >
            {({ setFieldValue }) => (
                <Form>
                    <PromptIfDirty />
                    <div className="relative">
                        <div className="sticky top-0 z-10">
                            <ArticleToolbar />
                        </div>
                        <div className="grid grid-cols-12 gap-4 px-2 sm:px-0">
                            <div className="order-last col-span-12 space-y-4 md:col-span-3">
                                <UuidField />
                                <ExportButton />
                                <HistoryButton />

                                <div className="space-y-4 rounded bg-white p-4 shadow-sm">
                                    <VersionStatusField />
                                    <StaticDateField
                                        label="Created At:"
                                        field="created_at"
                                        defaultValue="Unsaved"
                                    />
                                    <StaticDateField
                                        label="Modified At:"
                                        field="modified_at"
                                        defaultValue="Unsaved"
                                    />
                                    <StaticDateField
                                        label="Published At:"
                                        field="published_at"
                                        defaultValue="Draft"
                                    />
                                    <hr />
                                    <LayoutTemplateField />
                                    <hr />
                                    <div className="mx-0 flex items-end justify-evenly space-x-4 px-0">
                                        <div className="flex-grow">
                                            <PublishButton />
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <WorkflowField />
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <TagField />
                                </div>
                            </div>
                            <div className="order-first col-span-12 space-y-4 md:col-span-9">
                                {!state.loading &&
                                    state.content.lock?.uuid &&
                                    state.content.lock?.user?.uuid !==
                                        auth.user.uuid && (
                                        <div className="mb-4 rounded bg-white p-4 text-sm font-normal text-gray-800 shadow-sm">
                                            <LockClosedIcon className="inline h-5 w-5" />{" "}
                                            Checked out by{" "}
                                            {state.content.lock.user.name} on{" "}
                                            {fromSqlDateTime(
                                                state.content.lock.created_at
                                            ).toFormat("ff")}
                                        </div>
                                    )}

                                <DominantMediaField
                                    label="dominantAttachment"
                                    name="dominantAttachment"
                                    disabled={!state.isUserEditable}
                                />
                                <UrlField />

                                {state.loading ? (
                                    <Fragment>
                                        <div>
                                            <Label>Title</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                        <div>
                                            <Label>Slug</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                        <div>
                                            <Label>URL Slug</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <FastTextInput
                                            label="Title"
                                            name="title"
                                        />
                                        <TextInput
                                            label="Slug"
                                            name="slug"
                                            placeholder="Leave blank to auto-generate"
                                            disabled={
                                                state.loading ||
                                                state.preformingStateChange ||
                                                !state.isUserEditable
                                            }
                                            onChange={(e) => {
                                                const newVal = e.target.value
                                                    .toLowerCase()
                                                    .replace(/ {2,}/g, " ")
                                                    .replace(/ /g, "-")
                                                    .replace(/[^a-z0-9-]/g, "");

                                                setFieldValue("slug", newVal);
                                            }}
                                            helpertext="Slugs are internal references and depend on your publication's general workflow."
                                        />
                                        <TextInput
                                            label="URL Slug"
                                            name="title_url"
                                            placeholder="Leave blank to auto-generate"
                                            disabled={
                                                state.loading ||
                                                state.preformingStateChange ||
                                                !state.isUserEditable
                                            }
                                            onChange={(e) => {
                                                const newVal = e.target.value
                                                    .toLowerCase()
                                                    .replace(/ {2,}/g, " ")
                                                    .replace(/ /g, "-")
                                                    .replace(/[^a-z0-9-]/g, "");

                                                setFieldValue(
                                                    "title_url",
                                                    newVal
                                                );
                                            }}
                                            helpertext="URL slugs are used to generate the URL for the article, they should be short but descriptive."
                                        />
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="relative z-20 mt-4">
                        {state.loading ? (
                            <div className="absolute top-0 left-0 z-50 h-full w-full bg-gray-50">
                                <div className="h-full w-full animate-pulse rounded bg-gray-300"></div>
                            </div>
                        ) : null}
                        <TabContainer>
                            <TabPanel label="Content">
                                <div className="space-y-4">
                                    <div>
                                        <Label>Content</Label>
                                        <ArticleEditor
                                            name="content"
                                            onCreateNewCopyRef={
                                                props.onCreateNewCopyRef
                                            }
                                            onRequestNavigate={
                                                props.onRequestNavigate
                                            }
                                            disabled={
                                                state.loading ||
                                                state.preformingStateChange ||
                                                !state.isUserEditable
                                            }
                                            clips={
                                                settings.settings?.clips || []
                                            }
                                            externalCss={
                                                settings.settings?.editorCss ||
                                                ""
                                            }
                                        />
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel label="Meta">
                                <ArticleMeta block="content" />
                                <ArticleMeta block="content-lower" />
                            </TabPanel>
                            <TabPanel label="SEO">
                                <div className="mx-auto mb-4 max-w-screen-md space-y-4 rounded bg-white p-4">
                                    <p className="prose-sm text-gray-800">
                                        CEO will generate sensible SEO data for
                                        you automatically, but you can override
                                        it here.
                                    </p>
                                </div>
                                <div className="space-y-4">
                                    <SocialPreviews />
                                    <div>
                                        <Label>SEO Headline/Title</Label>
                                        <TextInput name="seo_title" />
                                    </div>
                                    <div>
                                        <Label>SEO Abstract/Description</Label>
                                        <TextArea
                                            name="seo_description"
                                            rows={3}
                                        />
                                    </div>
                                    <div>
                                        <Label>SEO Image</Label>
                                        <TextInput name="seo_url" />
                                    </div>
                                </div>
                            </TabPanel>
                        </TabContainer>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default EditForm;
